<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { detailConfigFor } from '../landProcessDetailConfig';
import DetailCardShell from './DetailCardShell.vue';
import DetailFieldGrid from './DetailFieldGrid.vue';
import DetailPartyListCard from './DetailPartyListCard.vue';
import DetailPaymentsCard from './DetailPaymentsCard.vue';
import DetailVerificationCard from './DetailVerificationCard.vue';
import DetailDocumentsCard from './DetailDocumentsCard.vue';
import DetailRemarksCard from './DetailRemarksCard.vue';
import ProcessDetailField from './ProcessDetailField.vue';

const props = defineProps({
  processName: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  data: { type: null, default: null },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const { isAdmin } = useAdmin();

const I18N_BASE = 'ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION';

function get(obj, path) {
  return path
    .split('.')
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function humanize(value) {
  if (!value) return '';
  return value
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

const config = computed(() => detailConfigFor(props.processName));

const title = computed(
  () => config.value?.staticTitle || humanize(props.processName)
);

const statusBadges = computed(() => {
  if (!props.data) return [];
  const badges = [];
  if (props.data.application_status) {
    badges.push(humanize(props.data.application_status));
  }
  if (props.data.node) badges.push(humanize(props.data.node));
  const submitted = props.data.submit_time || props.data.date_created;
  if (submitted) {
    const date = new Date(submitted);
    if (!Number.isNaN(date.getTime())) {
      badges.push(`${t(`${I18N_BASE}.SUBMITTED`)} ${date.toLocaleString()}`);
    }
  }
  return badges;
});

const summaryFields = computed(() => {
  if (!config.value) return [];
  return config.value.summaryFields.map(field => ({
    label: t(field.labelKey),
    value: get(props.data, field.key),
    format: field.format,
  }));
});

const pills = computed(() => {
  if (!config.value) return [];
  return config.value.pills
    .filter(pill => get(props.data, pill.key) !== undefined)
    .map(pill => ({
      label: t(pill.labelKey),
      active: !!get(props.data, pill.key),
    }));
});

const partyGroups = computed(() => {
  if (!config.value || !props.data) return [];
  return config.value.partyGroups
    .map(group => ({
      key: group.key,
      label: t(group.labelKey),
      roleLabel: group.roleLabel,
      items: get(props.data, group.key) || [],
    }))
    .filter(group => group.items.length);
});

const stampDutyFields = computed(() => {
  const sd = config.value?.stampDuty;
  if (!sd || !props.data) return [];
  return [
    {
      label: t(`${I18N_BASE}.STATUS`),
      value: humanize(get(props.data, sd.statusKey)),
    },
    {
      label: t(`${I18N_BASE}.STAMP_DUTY_APPLICATION_STATUS`),
      value: humanize(get(props.data, sd.applicationStatusKey)),
    },
    {
      label: t(`${I18N_BASE}.EXEMPTION_SECTION`),
      value: humanize(get(props.data, sd.sectionKey)),
    },
  ];
});

const stampDutyBasis = computed(() => {
  const sd = config.value?.stampDuty;
  return sd ? get(props.data, sd.basisKey) : '';
});

const hasStampDuty = computed(() => stampDutyFields.value.some(f => f.value));

const invoices = computed(() => props.data?.invoices || []);
const documents = computed(() => props.data?.documents || []);
const signatures = computed(() => props.data?.signatures || []);
const otps = computed(() => props.data?.otps || props.data?.otp || []);
const remarks = computed(
  () => props.data?.remarks || props.data?.applicant_remarks || []
);

function personFromField(key, labelKey) {
  const raw = get(props.data, key);
  if (!raw) return null;
  if (key === 'drawn_by') {
    return {
      names: raw.law_firm_name,
      phone_number: raw.phone_number,
      role: t(labelKey),
    };
  }
  return { ...raw, role: t(labelKey) };
}

const people = computed(() => {
  if (!config.value || !props.data) return [];
  return config.value.people
    .map(person => personFromField(person.key, person.labelKey))
    .filter(Boolean);
});

// Anything not claimed by a configured section still renders via the raw
// recursive field renderer, so nothing silently disappears for processes
// without a full config yet.
const isConfigured = computed(() => !!config.value);
</script>

<template>
  <div class="fixed inset-0 z-40 flex justify-end">
    <div class="absolute inset-0 bg-black/20" @click="emit('close')" />
    <div
      class="relative z-50 flex h-full w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl"
    >
      <div
        class="flex flex-shrink-0 items-start justify-between bg-[#71481d] px-6 py-5 text-white"
      >
        <div>
          <p class="text-xs uppercase tracking-wide text-white/70">
            {{ t(`${I18N_BASE}.DETAIL_TITLE`) }}
          </p>
          <h2 class="text-xl font-semibold">{{ title }}</h2>
          <div v-if="statusBadges.length" class="mt-2 flex flex-wrap gap-2">
            <span
              v-for="badge in statusBadges"
              :key="badge"
              class="rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium"
            >
              {{ badge }}
            </span>
          </div>
        </div>
        <button
          class="rounded p-1 text-white/80 hover:bg-white/10"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <div v-if="loading" class="flex flex-1 items-center justify-center">
        <span
          class="i-lucide-loader-circle size-6 animate-spin text-gray-400"
        />
      </div>

      <div v-else-if="error" class="flex-1 p-5 text-sm text-red-600">
        {{ error }}
      </div>

      <div v-else class="flex-1 overflow-y-auto p-6">
        <div v-if="!isConfigured">
          <ProcessDetailField :value="data" />
        </div>
        <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class="space-y-4">
            <DetailCardShell
              icon="i-lucide-file-text"
              :title="t(`${I18N_BASE}.APPLICATION_SUMMARY`)"
            >
              <DetailFieldGrid :fields="summaryFields" :pills="pills" />
            </DetailCardShell>

            <DetailCardShell
              v-if="isAdmin && documents.length"
              icon="i-lucide-folder"
              :title="t(`${I18N_BASE}.DOCUMENTS`)"
              :count="documents.length"
            >
              <DetailDocumentsCard :documents="documents" />
            </DetailCardShell>

            <DetailCardShell
              v-if="remarks.length"
              icon="i-lucide-message-square"
              :title="t(`${I18N_BASE}.REMARKS`)"
              :count="remarks.length"
            >
              <DetailRemarksCard :remarks="remarks" />
            </DetailCardShell>

            <DetailCardShell
              v-for="group in partyGroups"
              :key="group.key"
              icon="i-lucide-users"
              :title="group.label"
              :count="group.items.length"
            >
              <DetailPartyListCard
                :people="group.items"
                :role-label="group.roleLabel"
              />
            </DetailCardShell>
          </div>

          <div class="space-y-4">
            <DetailCardShell
              v-if="invoices.length"
              icon="i-lucide-credit-card"
              :title="t(`${I18N_BASE}.PAYMENTS`)"
              :count="invoices.length"
            >
              <DetailPaymentsCard :invoices="invoices" />
            </DetailCardShell>

            <DetailCardShell
              v-if="hasStampDuty"
              icon="i-lucide-landmark"
              :title="t(`${I18N_BASE}.STAMP_DUTY`)"
            >
              <DetailFieldGrid
                :fields="stampDutyFields"
                :note="stampDutyBasis"
              />
            </DetailCardShell>

            <DetailCardShell
              v-if="signatures.length || otps.length"
              icon="i-lucide-shield-check"
              :title="t(`${I18N_BASE}.SIGNATURES_OTP`)"
            >
              <DetailVerificationCard :signatures="signatures" :otps="otps" />
            </DetailCardShell>

            <DetailCardShell
              v-if="people.length"
              icon="i-lucide-user"
              :title="t(`${I18N_BASE}.PEOPLE`)"
            >
              <DetailPartyListCard :people="people" />
            </DetailCardShell>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
